import React, { useMemo, useState } from "react";
import {
  BLAST_RADIUS_CLASS,
  BLAST_RADIUS_SCHEMA_VERSION,
  formatBlastRadiusStateEntries,
  formatCapabilityCode,
  getBlastRadiusClassMeta,
  getMeaningfulClassCounts,
  getVisibleBlastRadiusBranches,
  isSupportedBlastRadiusSchema,
  summarizeBlastRadius,
} from "../helpers/clientBlastRadius";
import { BiohazardMark } from "./icons/BiohazardIcon";
import atomicBiohazardCore from "../assets/atomic/atomic-biohazard-core.png";
import "./ClientBlastRadiusScreen.css";

const LEGEND_CLASSES = [
  BLAST_RADIUS_CLASS.BLOCK,
  BLAST_RADIUS_CLASS.DELETE,
  BLAST_RADIUS_CLASS.REVERSE,
  BLAST_RADIUS_CLASS.PRESERVE,
  BLAST_RADIUS_CLASS.EXTERNAL_CLEANUP,
];

const LEGEND_HINTS = {
  [BLAST_RADIUS_CLASS.BLOCK]: "Cannot safely remove",
  [BLAST_RADIUS_CLASS.DELETE]: "Will be deleted",
  [BLAST_RADIUS_CLASS.REVERSE]: "Will be reversed",
  [BLAST_RADIUS_CLASS.PRESERVE]: "Will be preserved",
  [BLAST_RADIUS_CLASS.EXTERNAL_CLEANUP]: "External cleanup",
};

function ClassChip({ classification, count = null, size = "md" }) {
  const meta = getBlastRadiusClassMeta(classification);
  return (
    <span
      className={`cbr-chip cbr-chip--${meta.tone} cbr-chip--${size}`}
      data-classification={classification}
    >
      {meta.short}
      {count != null ? <span className="cbr-chip__count">{count}</span> : null}
    </span>
  );
}

function BranchNodeCard({ branch, onOpenDetails }) {
  const meta = getBlastRadiusClassMeta(branch.classification);
  return (
    <button
      type="button"
      className={`cbr-node cbr-node--${meta.tone}`}
      onClick={() => onOpenDetails?.(branch.id)}
      data-testid={`cbr-branch-card-${branch.id}`}
    >
      <div className="cbr-node__top">
        <div className="cbr-node__label">{branch.label || branch.entity}</div>
      </div>
      <div className="cbr-node__count">{Number(branch.count) || 0}</div>
      <ClassChip classification={branch.classification} size="sm" />
    </button>
  );
}

function OrbitConnectors({ count }) {
  const total = Math.max(count, 1);
  const radius = 38;
  const lines = Array.from({ length: count }, (_, index) => {
    const deg = -90 + (360 * index) / total;
    const rad = (deg * Math.PI) / 180;
    const x2 = 50 + radius * Math.cos(rad);
    const y2 = 50 + radius * Math.sin(rad);
    return { index, x2, y2 };
  });

  return (
    <svg
      className="cbr-orbit__connectors"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      {lines.map((line) => (
        <g key={line.index}>
          <line
            className="cbr-orbit__connector"
            x1="50"
            y1="50"
            x2={line.x2}
            y2={line.y2}
          />
          <circle
            className="cbr-orbit__connector-dot"
            cx={line.x2}
            cy={line.y2}
            r="1.1"
          />
        </g>
      ))}
    </svg>
  );
}

function BranchDetailPanel({ branch, expanded, onToggle }) {
  const meta = getBlastRadiusClassMeta(branch.classification);
  const states = formatBlastRadiusStateEntries(branch.states);
  const children = Array.isArray(branch.children) ? branch.children : [];
  const ops = Array.isArray(branch.required_operations)
    ? branch.required_operations
    : [];

  return (
    <div
      className={`cbr-detail-card cbr-detail-card--${meta.tone}`}
      data-testid={`cbr-branch-detail-${branch.id}`}
    >
      <button
        type="button"
        className="cbr-detail-card__header"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`cbr-branch-toggle-${branch.id}`}
      >
        <div className="cbr-detail-card__identity">
          <div className="cbr-detail-card__title">
            {branch.label || branch.entity}
          </div>
          <div className="cbr-detail-card__sub">
            {branch.entity}
            {branch.fk_delete_behavior ? ` · FK ${branch.fk_delete_behavior}` : ""}
          </div>
        </div>
        <div className="cbr-detail-card__meta">
          <span className="cbr-detail-card__count">
            {Number(branch.count) || 0}
          </span>
          <ClassChip classification={branch.classification} size="sm" />
          <span className="cbr-detail-card__chevron" aria-hidden>
            {expanded ? "▾" : "▸"}
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="cbr-detail-card__body">
          {branch.reason ? (
            <p className="cbr-detail-card__reason">{branch.reason}</p>
          ) : null}

          {branch.blocking ? (
            <div className="cbr-banner cbr-banner--warn">
              Blocking for Atomic Destruction today.
            </div>
          ) : null}

          {branch.external_cleanup ? (
            <div className="cbr-banner cbr-banner--external">
              External cleanup required (outside Postgres).
            </div>
          ) : null}

          {states.length > 0 ? (
            <div className="cbr-states">
              <div className="cbr-section-label">Lifecycle states</div>
              <ul>
                {states.map((s) => (
                  <li key={s.key}>
                    <strong>{s.label}</strong>
                    <span>{s.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {ops.length > 0 ? (
            <div className="cbr-ops">
              <div className="cbr-section-label">Required operations</div>
              <div className="cbr-ops__list">
                {ops.map((op) => (
                  <code key={String(op)}>{String(op)}</code>
                ))}
              </div>
            </div>
          ) : null}

          {children.length > 0 ? (
            <div className="cbr-children">
              <div className="cbr-section-label">Child dependencies</div>
              {children.map((child) => (
                <div key={child.id || child.entity} className="cbr-child-row">
                  <div>
                    <div className="cbr-child-row__title">
                      {child.label || child.entity}
                    </div>
                    {child.reason ? (
                      <div className="cbr-child-row__reason">{child.reason}</div>
                    ) : null}
                    {formatBlastRadiusStateEntries(child.states).length > 0 ? (
                      <div className="cbr-child-row__states">
                        {formatBlastRadiusStateEntries(child.states).map((s) => (
                          <span key={s.key}>
                            {s.label}: {s.count}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="cbr-child-row__meta">
                    <span>{Number(child.count) || 0}</span>
                    <ClassChip classification={child.classification} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Read-only Atomic Client Destruction Blast Radius UI (Phase 2A).
 * Overview + Details only — no arm/execute controls.
 */
export function ClientBlastRadiusScreen({
  payload,
  clientName,
  onBackToClient,
  initialView = "overview",
  focusBranchId = null,
}) {
  const [view, setView] = useState(
    initialView === "details" ? "details" : "overview",
  );
  const [expanded, setExpanded] = useState(() => {
    if (focusBranchId) return { [focusBranchId]: true };
    return {};
  });

  const supported = isSupportedBlastRadiusSchema(payload);
  const summary = useMemo(() => summarizeBlastRadius(payload), [payload]);
  const classRows = useMemo(
    () => getMeaningfulClassCounts(summary.byClass),
    [summary.byClass],
  );
  const visibleBranches = useMemo(
    () => getVisibleBlastRadiusBranches(payload),
    [payload],
  );
  const allBranches = Array.isArray(payload?.branches) ? payload.branches : [];
  const resolvedClientName =
    clientName || payload?.client?.client_name || "Client";

  const blockCount = Number(summary.byClass?.[BLAST_RADIUS_CLASS.BLOCK] || 0);
  const preserveCount = Number(
    summary.byClass?.[BLAST_RADIUS_CLASS.PRESERVE] || 0,
  );
  const secondaryClassRows = classRows.filter(
    (row) =>
      row.classification !== BLAST_RADIUS_CLASS.BLOCK &&
      row.classification !== BLAST_RADIUS_CLASS.PRESERVE,
  );

  if (!supported) {
    return (
      <div className="cbr-screen" data-testid="cbr-unsupported">
        <header className="cbr-header">
          <button type="button" className="cbr-link-back" onClick={onBackToClient}>
            ← Back to Client
          </button>
          <div className="cbr-eyebrow">
            <BiohazardMark size={14} className="cbr-eyebrow__mark" />
            <span>Atomic Client Destruction</span>
          </div>
          <h1>Permanent Client Deletion</h1>
        </header>
        <div className="cbr-unsupported">
          <h2>Unsupported blast-radius planner</h2>
          <p>
            This client returned an unexpected planner schema
            {payload?.schema_version != null
              ? ` (version ${payload.schema_version})`
              : ""}
            . Expected schema_version {BLAST_RADIUS_SCHEMA_VERSION}. Nothing was
            changed.
          </p>
        </div>
      </div>
    );
  }

  const invoices = allBranches.find((b) => b.id === "invoices");
  const timeLogs = allBranches.find((b) => b.id === "time_logs");
  const workspace = allBranches.find((b) => b.id === "workspace");
  const timeMeta = getBlastRadiusClassMeta(timeLogs?.classification);
  const workspaceMeta = getBlastRadiusClassMeta(workspace?.classification);

  return (
    <div className="cbr-screen" data-testid="cbr-screen">
      <header className="cbr-header">
        <div className="cbr-header__nav">
          {view === "details" ? (
            <button
              type="button"
              className="cbr-link-back"
              onClick={() => setView("overview")}
              data-testid="cbr-back-overview"
            >
              ← Back to Overview
            </button>
          ) : (
            <button
              type="button"
              className="cbr-link-back"
              onClick={onBackToClient}
              data-testid="cbr-back-client"
            >
              ← Back to Client
            </button>
          )}
          <span className="cbr-readonly-pill">
            <span className="cbr-icon-lock" aria-hidden />
            Read-only analysis
          </span>
        </div>

        <div className="cbr-eyebrow">
          <BiohazardMark size={14} className="cbr-eyebrow__mark" />
          <span>Atomic Client Destruction</span>
        </div>
        <h1>
          {view === "details"
            ? "Blast Radius Details"
            : "Permanent Client Deletion"}
        </h1>
        <p className="cbr-client-name">
          <span className="cbr-client-name__label">Client:</span>
          <span className="cbr-client-name__value">{resolvedClientName}</span>
        </p>
        <p className="cbr-lede">
          {view === "overview"
            ? "Review the blast radius before proceeding."
            : "Inspect the systems inside the blast radius. Expand a branch for states, operations, and children."}
        </p>

        <div className="cbr-hazard-stripe" aria-hidden />

        <div className="cbr-reassure" data-testid="cbr-nothing-changed">
          <div className="cbr-reassure__col">
            <span className="cbr-icon-shield" aria-hidden />
            <div>
              <strong>Nothing has been changed.</strong>
              <span>
                This is a read-only analysis of everything connected to this
                client.
              </span>
            </div>
          </div>
          <div className="cbr-reassure__col">
            <span className="cbr-icon-check" aria-hidden />
            <div>
              <strong>No data will be modified.</strong>
              <span>You are safe to explore.</span>
            </div>
          </div>
        </div>
      </header>

      {view === "overview" ? (
        <div className="cbr-overview" data-testid="cbr-overview">
          <section className="cbr-viz" aria-label="Blast radius visualization">
            <div
              className="cbr-orbit"
              data-branch-count={visibleBranches.length}
            >
              <div className="cbr-orbit__grid" aria-hidden />
              <div className="cbr-orbit__rings" aria-hidden>
                <span className="cbr-orbit__ring cbr-orbit__ring--a" />
                <span className="cbr-orbit__ring cbr-orbit__ring--b" />
                <span className="cbr-orbit__ring cbr-orbit__ring--c" />
                <span className="cbr-orbit__ring cbr-orbit__ring--d" />
              </div>
              <OrbitConnectors count={visibleBranches.length} />

              {visibleBranches.map((branch, index) => (
                <div
                  key={branch.id}
                  className={`cbr-orbit__slot cbr-orbit__slot--${index % 8}`}
                  style={{
                    "--cbr-slot-index": index,
                    "--cbr-slot-total": Math.max(visibleBranches.length, 1),
                  }}
                >
                  <BranchNodeCard
                    branch={branch}
                    onOpenDetails={() => {
                      setExpanded((prev) => ({ ...prev, [branch.id]: true }));
                      setView("details");
                    }}
                  />
                </div>
              ))}

              <div className="cbr-core" data-testid="cbr-core">
                <div className="cbr-core__mark">
                  <img
                    className="cbr-core__art"
                    src={atomicBiohazardCore}
                    alt=""
                    role="presentation"
                    draggable={false}
                    data-testid="cbr-core-biohazard-art"
                  />
                </div>
                <div className="cbr-core__kicker">Atomic Destruction</div>
                <div className="cbr-core__status">{summary.overallStatus}</div>
                <div className="cbr-core__analysis">
                  <span className="cbr-icon-lock" aria-hidden />
                  Read-only analysis
                </div>
              </div>
            </div>

            <div className="cbr-legend" aria-label="Classification legend">
              {LEGEND_CLASSES.map((classification) => {
                const meta = getBlastRadiusClassMeta(classification);
                return (
                  <div
                    key={classification}
                    className={`cbr-legend__item cbr-legend__item--${meta.tone}`}
                  >
                    <ClassChip classification={classification} size="sm" />
                    <span>{LEGEND_HINTS[classification]}</span>
                  </div>
                );
              })}
            </div>

            {visibleBranches.length === 0 ? (
              <p className="cbr-empty-branches">
                No attached dependency records were counted. Permanent delete of an
                unused client remains available from the client Lifecycle panel.
              </p>
            ) : null}
          </section>

          <section className="cbr-impact" data-testid="cbr-impact">
            <div className="cbr-panel-kicker">Impact Summary</div>
            <div className="cbr-impact__metric">
              <strong
                className="cbr-impact__metric-value"
                data-testid="cbr-total-records"
              >
                {summary.totalRecords}
              </strong>
              <span className="cbr-impact__metric-label">
                Total records in blast radius
              </span>
            </div>

            <div className="cbr-impact__highlights">
              {blockCount > 0 ? (
                <div
                  className="cbr-impact-highlight cbr-impact-highlight--danger"
                  data-testid={`cbr-class-${BLAST_RADIUS_CLASS.BLOCK}`}
                >
                  <BiohazardMark size={14} className="cbr-impact-highlight__mark" />
                  <span>Cannot Safely Remove</span>
                  <strong>{blockCount}</strong>
                </div>
              ) : null}
              {preserveCount > 0 ? (
                <div
                  className="cbr-impact-highlight cbr-impact-highlight--preserve"
                  data-testid={`cbr-class-${BLAST_RADIUS_CLASS.PRESERVE}`}
                >
                  <span className="cbr-icon-shield cbr-icon-shield--sm" aria-hidden />
                  <span>Will Be Preserved</span>
                  <strong>{preserveCount}</strong>
                </div>
              ) : null}
            </div>

            {secondaryClassRows.length > 0 ? (
              <div className="cbr-impact__chips">
                {secondaryClassRows.map((row) => (
                  <div
                    key={row.classification}
                    className={`cbr-impact-chip cbr-impact-chip--${row.tone}`}
                    data-testid={`cbr-class-${row.classification}`}
                  >
                    <span>{row.label}</span>
                    <strong>{row.count}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="cbr-readiness" data-testid="cbr-readiness">
            <div className="cbr-panel-kicker">Destruction readiness</div>
            {!summary.canExecute ? (
              <div className="cbr-readiness__row">
                <div className="cbr-readiness__state" aria-label={summary.overallStatus}>
                  <span className="cbr-readiness__x" aria-hidden>
                    ×
                  </span>
                  <span className="cbr-readiness__badge">
                    {summary.overallStatus}
                  </span>
                </div>
                <p>
                  Atomic Destruction cannot run for this client today. CrewPilot
                  identified dependencies and/or missing lifecycle operations that
                  must be safely resolved before destruction could ever be
                  authorized. This is informational — nothing was changed.
                </p>
              </div>
            ) : (
              <p>
                Planner reported execution readiness, but Phase 2A does not expose
                arm/execute controls.
              </p>
            )}
          </section>

          {summary.missingCapabilities.length > 0 ? (
            <section className="cbr-blockers" data-testid="cbr-blockers">
              <div className="cbr-panel-kicker">Safety subsystems</div>
              <h2>Why destruction is currently blocked</h2>
              <ul>
                {summary.missingCapabilities.map((gap) => (
                  <li key={gap.code || gap.message}>
                    <div className="cbr-blocker__header">
                      <span className="cbr-icon-warn" aria-hidden />
                      {gap.severity ? (
                        <span
                          className={`cbr-severity cbr-severity--${gap.severity}`}
                        >
                          {gap.severity === "hard" ? "Hard blocker" : gap.severity}
                        </span>
                      ) : (
                        <span className="cbr-severity cbr-severity--hard">
                          Hard blocker
                        </span>
                      )}
                    </div>
                    <div className="cbr-blocker__title">
                      {formatCapabilityCode(gap.code)}
                    </div>
                    <p>{gap.message}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {(Number(invoices?.states?.paid) > 0 ||
            invoices?.classification === "REVERSE" ||
            Number(timeLogs?.count) > 0 ||
            Number(workspace?.count) > 0) && (
            <div className="cbr-callouts">
              {(Number(invoices?.states?.paid) > 0 ||
                invoices?.classification === "REVERSE") && (
                <section
                  className="cbr-callout cbr-callout--finance"
                  data-testid="cbr-finance"
                >
                  <div className="cbr-callout__kicker">Reverse — not delete</div>
                  <h3>Financial reversal is required</h3>
                  <p>
                    Paid invoices require compensating finance events before any
                    commercial unwind. Posted finance events and movements are
                    preserved historical records — they are never hard-deleted.
                  </p>
                </section>
              )}

              {Number(timeLogs?.count) > 0 && (
                <section
                  className={`cbr-callout cbr-callout--payroll cbr-callout--${timeMeta.tone}`}
                  data-testid="cbr-payroll"
                >
                  <div className="cbr-callout__kicker">
                    {timeLogs?.classification === "PRESERVE"
                      ? "Protected history"
                      : timeLogs?.classification || "Time / payroll"}
                  </div>
                  <h3>Payroll / time history</h3>
                  <p>
                    Time logs may include unpaid operational entries and paid or
                    invoice-allocated payroll history. Preserved and blocked payroll
                    history is not treated as safely deletable.
                  </p>
                </section>
              )}

              {Number(workspace?.count) > 0 && (
                <section
                  className={`cbr-callout cbr-callout--workspace cbr-callout--${workspaceMeta.tone}`}
                  data-testid="cbr-workspace"
                >
                  <div className="cbr-callout__kicker">
                    {workspace?.external_cleanup
                      ? "External cleanup"
                      : workspace?.classification || "Workspace"}
                  </div>
                  <h3>Client Workspace</h3>
                  <p>
                    Workspace spaces, engagements, approvals, and external
                    workspace-assets storage may be part of this blast radius. Storage
                    cleanup sits outside the Postgres transaction graph.
                  </p>
                </section>
              )}
            </div>
          )}

          <div className="cbr-actions">
            <button
              type="button"
              className="cbr-btn cbr-btn--primary cbr-btn--stack"
              onClick={() => setView("details")}
              data-testid="cbr-view-full"
            >
              <BiohazardMark size={18} className="cbr-btn__mark" />
              <span className="cbr-btn__copy">
                <span className="cbr-btn__title">View Full Blast Radius</span>
                <span className="cbr-btn__sub">Inspect every dependency branch</span>
              </span>
              <span className="cbr-btn__chevron" aria-hidden>
                ›
              </span>
            </button>
            <button
              type="button"
              className="cbr-btn cbr-btn--stack"
              onClick={onBackToClient}
            >
              <span className="cbr-btn__copy">
                <span className="cbr-btn__title">Back to Client</span>
                <span className="cbr-btn__sub">Return to client lifecycle</span>
              </span>
              <span className="cbr-btn__chevron" aria-hidden>
                ›
              </span>
            </button>
            <div className="cbr-actions__note">
              <span className="cbr-icon-lock" aria-hidden />
              Read-only analysis — you cannot modify anything in this view.
            </div>
          </div>
        </div>
      ) : (
        <div className="cbr-details" data-testid="cbr-details">
          <div className="cbr-details__identity">
            <BiohazardMark size={22} className="cbr-details__mark" />
            <div>
              <div className="cbr-panel-kicker">Blast radius details</div>
              <p className="cbr-details__client">{resolvedClientName}</p>
            </div>
            <span className="cbr-readonly-pill cbr-readonly-pill--compact">
              Read-only
            </span>
          </div>
          <p className="cbr-details__lede">
            Inspect every major dependency branch. Expand a branch to see states,
            reasons, required operations, and children.
          </p>
          <div className="cbr-details__list">
            {allBranches.map((branch) => (
              <BranchDetailPanel
                key={branch.id}
                branch={branch}
                expanded={Boolean(expanded[branch.id])}
                onToggle={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [branch.id]: !prev[branch.id],
                  }))
                }
              />
            ))}
          </div>
          <div className="cbr-actions">
            <button
              type="button"
              className="cbr-btn"
              onClick={() => setView("overview")}
            >
              Back to Overview
            </button>
            <button type="button" className="cbr-btn" onClick={onBackToClient}>
              Back to Client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientBlastRadiusScreen;
